/**
 * Схемы ответов слушателя `ui`. 09 §6.1 (таблица маршрутов), §9.1 (модель рендера),
 * §9.3 (член `publication`), §9.4 (расписание).
 *
 * Эти схемы существуют не для документации: UI разбирает ими КАЖДЫЙ ответ бэкенда у себя,
 * и провал разбора модели рендера — блокирующая ошибка уровня 1 (09 §9.1), при которой все
 * три кнопки решения выключены. Отсюда два следствия, определяющих весь файл:
 *
 *   1. Файл обязан быть импортируем браузером: ни одного `node:*`, ничего кроме zod и
 *      соседних модулей `@smm/contracts` (правило R8 стража границ).
 *   2. Объекты закрыты (`z.strictObject`). Лишний ключ — это либо бэкенд, ушедший вперёд
 *      схемы, либо опечатка; молча пропущенный, он превращается в поле, которого UI не
 *      показывает, и никто об этом не узнаёт.
 *
 * Канонический контент НЕ переобъявляется: `canonical` — это `DraftContentSchema` из
 * `../telegram/draft.ts`. Второе описание той же формы дало бы второй источник правды,
 * и именно оно бы разошлось. Побочный эффект нужный: `cv` там — `z.literal(CANON_ALGO_VERSION)`,
 * поэтому версия, посчитанная другим алгоритмом канонизации, не разбирается — а это ровно
 * та политика «закрываться при расхождении», ради которой `cv` вообще выведен в ответ.
 */

import * as z from 'zod';
import { CHAT_ID_RE, CONTENT_HASH_RE, NONCE_RE } from '../approval-key/limits.ts';
import { DRAFT_STATES } from '../draft-state.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISSUE_CODES } from '../issues.ts';
import { DraftContentSchema, MessageEntitySchema } from '../telegram/draft.ts';
import { ISO_UTC_MS } from '../time.ts';
import { AUTHOR_OPINION_MAX_CODEPOINTS, AuthorOpinionViewSchema } from './author-opinion.ts';
import { VersionAutoApprovalSchema } from './auto-approval.ts';
import { BRAND_THEMES } from './brand-config.ts';
import { FORMAT_CODE_RE } from './content-plan.ts';
import { PosterSpecSchema } from './poster.ts';
import { TestSendSchema } from './transport.ts';
import {
  MEDIA_ORIGINS,
  VISUAL_PROPOSAL_STATES,
  VISUAL_VERDICTS,
  VisualLayoutSchema,
} from './visual.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);
const ContentHash = z.string().regex(CONTENT_HASH_RE);
const DraftState = z.enum(DRAFT_STATES);
/** `draft_version.origin` — CHECK таблицы, 03-storage §DDL. */
const DraftOrigin = z.enum(['writer', 'human_edit', 'fixture']);

/**
 * Сквозной номер поста В БРЕНДЕ: «post #7». Считается бэкендом из порядка создания
 * черновиков (`read-model.ts` → `POST_NO_SQL`), а не хранится: колонки под него нет, и
 * миграция ради подписи не нужна. Номер стабилен, потому что черновик не удаляется никогда —
 * «удалить» переводит версию в `discarded`, строка остаётся и продолжает держать свой номер.
 * Поэтому в списке без архива номера идут с пропусками, и это правда, а не сбой.
 *
 * До 10.09.2026 в этой роли были восемь знаков UUID («news #01a08797»): они отвечали на
 * «на что сослаться в переписке», но не на «который это пост».
 */
export const PostNo = z.int().positive();

/**
 * Номер поста словами: «post #7». Одна функция на бэкенд и интерфейс — шапка черновика,
 * подзаголовки диалогов, строка бэклога и последний рубеж заголовка обязаны называть пост
 * одинаково, а две копии литерала разошлись бы первой же правкой.
 */
export function postLabel(postNo: number): string {
  return `post #${String(postNo)}`;
}

/**
 * Локальная минута слота, 'YYYY-MM-DDTHH:MM'. Копия приватной LOCAL_MINUTE из `../time.ts`:
 * там она не экспортируется, а тянуть ради формы строки экспорт из дома времени — значит
 * расширять его поверхность. Расхождение невозможно скрыто: обе стороны описывают формат,
 * который пишет одна колонка `publication_slot.slot_local`.
 *
 * Экспортирована ради `./operations.ts` (создание слота руками): третья копия одной строки
 * формата внутри одного пакета разошлась бы первой же правкой.
 */
export const LOCAL_MINUTE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/v1/auth/session

/**
 * Роль приходит из `app_user.role` (CHECK: approver | admin). Список закрыт: новая роль
 * без правки UI означала бы экран, который не знает, что человеку можно.
 */
export const SessionUserSchema = z.strictObject({
  id: Uuid,
  login: z.string().min(1),
  display_name: z.string(),
  role: z.enum(['approver', 'admin']),
});
export type SessionUser = z.output<typeof SessionUserSchema>;

export const SessionResponseSchema = z.strictObject({
  user: SessionUserSchema,
  expires_at: IsoUtcMs,
});
export type SessionResponse = z.output<typeof SessionResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/v1/brands

/**
 * Какие сервисы обслуживают бренд (docs/multi-brand.md §8). Бренд может жить на установке
 * одним интерфейсом — без мониторинга и генерации (AGI Boardroom до подключения): решения,
 * правка и публикация у него работают, но новых черновиков нет (их пишет только сервис
 * генерации), а заявку на обход или пост некому взять. Без этих флагов интерфейс
 * предлагал бы кнопки, чьи заявки встают в очередь навсегда.
 *
 * Флаги — те же признаки, по которым сервисы сами выбирают бренды, и ничего больше:
 *   • `monitoring` — `monitoring.enabled: true` в конфиге (`agent-monitor/src/main.ts`);
 *   • `generation` — в конфиге есть секция `generation` (`agent-writer/src/config.ts`).
 * Генерация НЕ требует мониторинга целиком: прогон по своему тексту (контекст) и переделка
 * идут к писателю мимо обхода. Мониторинг нужен обычной заявке кнопки и заказу из таблицы —
 * это проверяет бэкенд на каждом пути (`domain/context.ts`, `assertBrandAgents`).
 */
export const BrandAgentsSchema = z.strictObject({
  monitoring: z.boolean(),
  generation: z.boolean(),
});
export type BrandAgents = z.output<typeof BrandAgentsSchema>;

/** Разрешение автоодобрения в списке брендов — см. `BrandSchema.auto_approval`. */
export const BrandAutoApprovalSchema = z.strictObject({
  allowed: z.boolean(),
});
export type BrandAutoApproval = z.output<typeof BrandAutoApprovalSchema>;

/**
 * Разрешение «Добавить мнение» в списке брендов — см. `BrandSchema.author_opinion`.
 *
 * `max_codepoints` — потолок сырого текста ЭТОГО бренда (`generation.author_opinion.max_codepoints`
 * конфига): по нему диалог считает знаки и запирает кнопку, а бэкенд отбивает длиннее. Конфиг
 * вправе опустить общий потолок, и без поля диалог обещал бы человеку `AUTHOR_OPINION_MAX_CODEPOINTS`
 * там, где сохранить можно меньше. Необязательно: нет поля — общий потолок.
 */
export const BrandAuthorOpinionSchema = z.strictObject({
  allowed: z.boolean(),
  max_codepoints: z.int().min(100).max(AUTHOR_OPINION_MAX_CODEPOINTS).optional(),
});
export type BrandAuthorOpinion = z.output<typeof BrandAuthorOpinionSchema>;

/**
 * `details.reason` отказа заявки к сервису, которого у бренда нет (409 `conflict`). Слово одно
 * на бэкенд и интерфейс: диалоги, читающие всякий 409 как «черновик изменился», по нему
 * отличают отказ бренда и показывают текст сервера.
 */
export const BRAND_AGENTS_OFF_REASON = 'brand_agents_off';

/**
 * `timezone` и `max_per_day` едут по HTTP не для полноты: у `packages/ui` из `@smm/*`
 * разрешён только `contracts` (правило R4), конфиг бренда ему недоступен физически, а без
 * зоны слот нечем подписать (09 §9.4).
 *
 * `channel_id` — канал ТЕКУЩЕГО режима публикаций ЭТОГО бренда (`resolveChannelId`; режим
 * у каждого бренда свой с 2026-10-14), а не «титул канала»: см. расхождение с §6.1 в шапке отчёта кластера. `null` — поле этого режима в
 * конфиге бренда пусто, и одобрение в нём откажет, назвав поле.
 */
export const BrandSchema = z.strictObject({
  id: z.string().min(1),
  title: z.string(),
  channel_id: z.string().regex(CHAT_ID_RE).nullable(),
  timezone: z.string().min(1),
  slots_per_week: z.int().nonnegative(),
  max_per_day: z.int().positive(),
  /**
   * Внешний вид бренда в интерфейсе (`ui` конфига бренда, docs/multi-brand.md §6). Умолчания
   * подставляет бэкенд: тема `geoboost`, вордмарк — `title`. Порядок в ответ не едет — им
   * отсортирован сам список.
   */
  theme: z.enum(BRAND_THEMES),
  wordmark: z.string().min(1),
  /** Сервисы бренда — `BrandAgentsSchema`. Обязательно: конфиг бренда бэкенд знает всегда. */
  agents: BrandAgentsSchema,
  /**
   * Разрешает ли конфиг бренда автоодобрение (`auto_approval.allowed`, замок 1 из 3,
   * docs/ai-lab.md). Только разрешение, не положение выключателя: его отдаёт
   * `GET /api/v1/auto-approval?brand=`. `false` — выключателя в шапке нет. Обязательно по той
   * же причине, что `agents`.
   */
  auto_approval: BrandAutoApprovalSchema,
  /**
   * Включено ли бренду «Добавить мнение» (`generation.author_opinion` конфига, AGI Boardroom,
   * 2026-11-02). `allowed: false` и отсутствие поля — полосы мнения нет. Необязательно, в
   * отличие от `auto_approval`: ответ бэкенда старше поля обязан разбираться, иначе экран закрыл
   * бы одобрение у ВСЕХ брендов (`useModeChannel` читает список строго).
   */
  author_opinion: BrandAuthorOpinionSchema.optional(),
  /**
   * Форматы, которые человек вправе выбрать кнопкой «Запустить генерацию» у ЭТОГО бренда, в
   * порядке библиотеки (форматы, подключённые его базой знаний; обзор конференции — только у
   * бренда со списком наблюдения). Нет поля — у интерфейса прежний общий список. Необязательно
   * по той же причине, что `author_opinion`.
   */
  selectable_formats: z.array(z.string().regex(FORMAT_CODE_RE).max(64)).optional(),
  /**
   * Размечает ли бренд организации, запрещённые в РФ (`legal.banned_orgs`): редактор применяет ту
   * же разметку к предпросмотру перед сохранением — только показ, решает бэкенд. Нет поля —
   * не размечает.
   */
  banned_orgs: z.boolean().optional(),
});
export type Brand = z.output<typeof BrandSchema>;

export const BrandsResponseSchema = z.array(BrandSchema);
export type BrandsResponse = z.output<typeof BrandsResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/v1/backlog

/**
 * Потолок заголовка строки бэклога В КОДПОИНТАХ. Не «сколько влезает в колонку» — вёрстка
 * сама решает, где обрезать визуально; это потолок ПЕРЕДАВАЕМОГО текста, чтобы список из
 * полусотни постов не вёз полсотни целых постов.
 */
export const BACKLOG_TITLE_MAX_CODEPOINTS = 70;

/**
 * `status` — статус ТЕКУЩЕЙ версии (соединение `draft.current_version_id`), а не черновика:
 * статусной машины у черновика нет, она у версии (09 §2).
 *
 * `title` НЕ nullable, и это ответ на жалобу заказчика 26.08 («в бэклоге случайные наборы
 * букв»): до неё строка подписывалась `fixture_id ?? draft_id.slice(0, 8)`, то есть
 * идентификатором. Заголовок считает бэкенд — заголовок темы, а без темы первая строка
 * канонического текста по границе слова. Пустым он не бывает никогда: у поста без темы и
 * без текста (подпись к картинке может быть пуста) остаётся «post #N», но это последний
 * рубеж, а не поведение по умолчанию. Nullable-поле здесь означало бы ветку «нечего
 * показать» в каждом месте UI, которое рисует список.
 */
/**
 * Стиль, которым написана ТЕКУЩАЯ версия (docs/release-2026-09-19.md §5.5). `null` —
 * версия старше стилей как данных или стиль неизвестен (фикстура). Имя — на момент ответа:
 * копию можно переименовать, и ярлык следует за ней.
 */
export const RenderStyleSchema = z.strictObject({
  version_id: Uuid,
  name: z.string().min(1),
  kind: z.enum(['release', 'custom']),
});
export type RenderStyle = z.output<typeof RenderStyleSchema>;

/**
 * Пара прогона сравнения стилей (§5.4): второй черновик той же заявки. Экран черновика
 * ведёт по ней на сравнение бок о бок, бэклог — метит оба поста одной парой.
 */
export const RenderComparisonSchema = z.strictObject({
  request_id: Uuid,
  /** Какая это половина: `a` — стиль заявки, `b` — стиль сравнения. */
  side: z.enum(['a', 'b']),
  other_draft_id: Uuid.nullable(),
});
export type RenderComparison = z.output<typeof RenderComparisonSchema>;

export const BacklogItemSchema = z.strictObject({
  draft_id: Uuid,
  /** Сквозной номер поста в бренде — см. `PostNo`. */
  post_no: PostNo,
  brand: z.string().min(1),
  current_version: z.int().positive(),
  status: DraftState,
  origin: DraftOrigin,
  fixture_id: z.string().nullable(),
  title: z.string().min(1),
  updated_at: IsoUtcMs,
  /** Непусто, только пока черновик занимает слот; освобождение слота обнуляет поле. */
  slot_at: IsoUtcMs.nullable(),
  /** Стиль текущей версии — см. `RenderStyleSchema`. */
  style: RenderStyleSchema.nullable(),
  /** Половина прогона сравнения стилей, если пост из него. */
  comparison: RenderComparisonSchema.nullable(),
  /**
   * Черновик рождён расписанием (автоновость / автодайджест), а не нажатием человека: тема
   * пришла из заявки мониторинга без заявки генерации. UI метит строку «авто».
   */
  auto: z.boolean(),
  /**
   * След АВТООДОБРЕНИЯ текущей версии (`VersionAutoApprovalSchema`, docs/ai-lab.md) — не то же,
   * что `auto` выше: `auto` — кто ПОРОДИЛ черновик, это поле — кто его ОДОБРИЛ или почему не
   * стал. Ключа нет у версий, которых автоодобрение не касалось.
   */
  auto_approval: VersionAutoApprovalSchema.optional(),
});
export type BacklogItem = z.output<typeof BacklogItemSchema>;

export const BacklogResponseSchema = z.array(BacklogItemSchema);
export type BacklogResponse = z.output<typeof BacklogResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/v1/drafts/:id

export const DraftHeadSchema = z.strictObject({
  draft_id: Uuid,
  brand: z.string().min(1),
  topic_id: Uuid.nullable(),
  /** NULL достижим ровно один момент — между вставкой черновика и его первой версией. */
  current_version_id: Uuid.nullable(),
  created_at: IsoUtcMs,
  updated_at: IsoUtcMs,
  /** След автоодобрения ТЕКУЩЕЙ версии; ключа нет, если оно её не касалось. */
  auto_approval: VersionAutoApprovalSchema.optional(),
});

export const DraftVersionRefSchema = z.strictObject({
  version_no: z.int().positive(),
  status: DraftState,
  origin: DraftOrigin,
  fixture_id: z.string().nullable(),
  /** `draft_version.created_by`: 'human:<uuid>' или 'service:<name>' — свободная строка. */
  actor: z.string(),
  created_at: IsoUtcMs,
});

export const DraftWithVersionsSchema = z.strictObject({
  draft: DraftHeadSchema,
  versions: z.array(DraftVersionRefSchema),
});
export type DraftWithVersions = z.output<typeof DraftWithVersionsSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/v1/drafts/:id/versions/:n — модель рендера (09 §9.1)

/**
 * Адрес байтов, вынесенный НАРУЖУ подписанного дескриптора: `media_id` — адрес, а не
 * содержимое, и в канонический хэш он не входит (00-integration §Хребет данных 2).
 * `width`/`height` в фазе 1 всегда null — размеры никто не измеряет, поле заведено, чтобы
 * появление измерителя не меняло форму ответа.
 */
export const MediaRefSchema = z.strictObject({
  media_id: Uuid,
  width: z.int().positive().nullable(),
  height: z.int().positive().nullable(),
});
export type MediaRef = z.output<typeof MediaRefSchema>;

/**
 * Адрес байтов документа (PDF внизу поста, 19.09.2026) — как `MediaRefSchema`: адрес, а не
 * содержимое, в хэш не входит. Имя и размер повторены здесь для строки «Файл» и блока
 * документа в превью: дескриптор в `canonical.document` несёт их же, но экран не обязан
 * лезть в подписываемую форму за подписью к значку.
 */
export const DocumentRefSchema = z.strictObject({
  media_id: Uuid,
  file_name: z.string().min(1),
  bytes: z.int().positive(),
});
export type DocumentRef = z.output<typeof DocumentRefSchema>;

/**
 * Строка списка правок канонизации.
 *
 * Форма — проекция `Issue` (`../issues.ts`): `{code, message}`. Пример в 09 §9.1 показывает
 * `{kind, rule, description}`, но это дословный перенос примера из `04-backend-http.md` §11.4,
 * который тот же §9.1 в остальном переписывает; поля `kind/rule/description` не порождает
 * ни один код, а нормативные пункты §9.1 форму строки не задают вовсе. Схема, описанная по
 * примеру, отвергала бы КАЖДЫЙ реальный ответ — то есть блокировала бы согласование всегда.
 */
export const CanonicalizationDiffEntrySchema = z.strictObject({
  code: z.enum(ISSUE_CODES),
  /** По-русски: показывается человеку в блоке «Что изменила канонизация». */
  message: z.string(),
});
export type CanonicalizationDiffEntry = z.output<typeof CanonicalizationDiffEntrySchema>;

/**
 * `DELETE /api/v1/drafts/:id/versions/:n/document` (docs/release-2026-09-19.md §9.2) — снять PDF.
 * Форма та же, что у правки текста (`services/backend/src/domain/human-edit.ts`,
 * `HumanEditResult.version`): снятие рождает версию тем же путём. `201` — новая версия без
 * файла; `200` — текущая, файла на ней уже нет (повтор нажатия, соседняя вкладка).
 *
 * Не `DocumentUploadResponseSchema`: у снятия нет ни `media_id`, ни имени, ни размера — схема
 * загрузки отвергала бы каждый ответ.
 */
export const DocumentRemoveResponseSchema = z.strictObject({
  draft_id: Uuid,
  version_id: Uuid,
  version_no: z.int().positive(),
  status: z.enum(DRAFT_STATES),
  content_hash: ContentHash,
  canonicalization_diff: z.array(CanonicalizationDiffEntrySchema),
});
export type DocumentRemoveResponse = z.output<typeof DocumentRemoveResponseSchema>;

/** Сырой вход до канонизации; приезжает из payload аудит-события создания версии. */
export const RawSubmittedSchema = z.strictObject({
  text: z.string(),
  entities: z.array(MessageEntitySchema),
});

// ─────────────────────────────────────────────────────────────────────────────
// Провенанс: тема и материалы, которыми она подтверждена (05-seams §1.6)

/**
 * Один материал темы в том виде, в каком его читает согласующий.
 *
 * `domain` считает БЭКЕНД, а не UI, и это не удобство: домен — величина, по которой человек
 * принимает решение («это vc.ru или anthropic.com?»), и вычислять её второй раз в браузере
 * значит завести второй ответ на один вопрос. Показывается он ровно так, как стоит в
 * каноническом адресе: ни `www.` не снимается, ни punycode не раскрывается. Раскрытие
 * punycode — это и есть омографная подмена, а снятие поддомена прячет ту часть адреса, ради
 * которой панель существует.
 *
 * `published_at_origin` едет наружу по той же причине: `published_at` тотален (§1.4,
 * `min(разобранная ?? fetched_at, fetched_at)`), поэтому дата есть ВСЕГДА — и без этого поля
 * подставленное время получения неотличимо от разобранной даты первоисточника.
 *
 * Ни лида, ни выжимки здесь нет: экран согласования показывает пост, а не пересказ статьи.
 */
export const RenderTopicMaterialSchema = z.strictObject({
  role: z.enum(['primary', 'supporting']),
  domain: z.string().min(1),
  url_canonical: z.string().min(1),
  published_at: IsoUtcMs,
  published_at_origin: z.enum(['source', 'fetched_at']),
});
export type RenderTopicMaterial = z.output<typeof RenderTopicMaterialSchema>;

/**
 * Тема черновика — подмножество `GET /intake/v1/topics/:id` (05-seams §1.6, §9.6): заголовок,
 * угол, обоснование, подсказка формата, дедлайн свежести и материалы.
 *
 * `materials` НЕ помечен `.min(1)` и «ровно один primary» здесь не проверяется, хотя маршрут
 * тем оба инварианта держит. Причина в цене отказа: провал разбора модели рендера выключает
 * все кнопки решения и показывает фразу про чужой алгоритм канонизации (09 §9.1) — то есть
 * тема без материалов уронила бы экран с неверным объяснением. Панель вместо этого говорит
 * словами, что источника нет, и решение остаётся за человеком.
 *
 * `status` и `origin` темы наружу не едут: согласующий решает по источнику и дате, а не по
 * тому, каким прогоном тема заведена. Поле, которое ни на что не влияет, — это поле, которое
 * разойдётся с БД молча.
 */
export const RenderTopicSchema = z.strictObject({
  topic_id: Uuid,
  title: z.string().min(1),
  angle: z.string().nullable(),
  rationale: z.string().nullable(),
  format_hint: z.string().nullable(),
  /** `null` ⟺ `freshness_policy: 'evergreen'` — политика выводится из дедлайна (`topics.ts`). */
  freshness_deadline: IsoUtcMs.nullable(),
  materials: z.array(RenderTopicMaterialSchema),
});
export type RenderTopic = z.output<typeof RenderTopicSchema>;

/** CHECK `publication_job.state`. */
export const PublicationJobStateSchema = z.enum([
  'scheduled',
  'leased',
  'sending',
  'sent',
  'verified',
  'failed',
  'needs_manual_check',
  'cancelled',
]);
export type PublicationJobState = z.output<typeof PublicationJobStateSchema>;

/**
 * Свидетельство автоматической проверки (09 §5). `null`, пока проверка не закрыта:
 * соединение берёт только строку в состоянии `done`.
 */
export const PublicationResolutionSchema = z.strictObject({
  result_kind: z.enum(['found', 'not_found', 'needs_human']),
  /** Непусто ⟺ result_kind === 'found' — держится CHECK'ом таблицы. */
  found_message_id: z.int().positive().nullable(),
  detail: z.string().nullable(),
  reported_at: IsoUtcMs.nullable(),
});
export type PublicationResolution = z.output<typeof PublicationResolutionSchema>;

/**
 * Технический след одной попытки. Отдельно от исхода работы: человеку показывают
 * `outcome_code`, а `audit` разворачивается по требованию — это материал для инженера.
 * `nonce` может быть пуст: ключ отзывают, и LEFT JOIN тогда не находит строки.
 */
export const PublicationAuditSchema = z.strictObject({
  attempt_id: Uuid,
  nonce: z.string().regex(NONCE_RE).nullable(),
  finished_at: IsoUtcMs.nullable(),
  /** Код Telegram строкой ('400') либо класс отказа транспорта. */
  failure_code: z.string().nullable(),
  failure_description: z.string().nullable(),
});
export type PublicationAudit = z.output<typeof PublicationAuditSchema>;

/**
 * Последний отзыв этой работы (09 §4.3, G-001). `null`, пока кнопку «отозвать» не нажимали.
 *
 * Поле обязано жить в модели рендера, а не только в таблице: отзыв — решение ЧЕЛОВЕКА, и
 * оно может не состояться без единого следа на экране. Публикатор, не забравший задание за
 * окно подписанного ключа, оставляет строку в `expired` — пост при этом остаётся в канале,
 * а экран «опубликован» без этого поля выглядит ровно так же, как если бы отзыва и не
 * просили. Состояния — те же четыре, что в CHECK таблицы `retraction`.
 */
export const PublicationRetractionSchema = z.strictObject({
  state: z.enum(['open', 'done', 'failed', 'expired']),
  outcome_code: z.string().nullable(),
  requested_at: IsoUtcMs,
  reason: z.string(),
});
export type PublicationRetraction = z.output<typeof PublicationRetractionSchema>;

/**
 * Член `publication` модели рендера (09 §9.3). `null`, пока работы публикации нет.
 *
 * `outcome_code` НЕ перечислен списком намеренно: таблица §9.3 наполняет его в том числе
 * значением `reason` из отчёта публикатора, то есть набор открыт. Фразу человеку UI берёт
 * по таблице §Ключ 7.5, а код вне таблицы показывает как есть — пустой экран хуже
 * незнакомого кода.
 */
export const PublicationSchema = z.strictObject({
  job_id: Uuid,
  job_state: PublicationJobStateSchema,
  /** Только для ссылки на канал (`https://t.me/c/${chat_id.slice(4)}`), 09 §9.3. */
  chat_id: z.string().regex(CHAT_ID_RE),
  /**
   * На какой канал подписана работа: режим публикаций, который стоял на одобрении
   * (`api/operations.ts`; значения повторены литералом, потому что operations.ts сам
   * импортирует этот файл). `null` — `chat_id` не совпадает ни с одним каналом конфига
   * бренда: канал сменили после одобрения, и такую работу не выдаст ни один режим.
   */
  channel: z.enum(['live', 'test']).nullable(),
  slot_start_at: IsoUtcMs,
  slot_deadline_at: IsoUtcMs,
  sending_started_at: IsoUtcMs.nullable(),
  telegram_message_id: z.int().positive().nullable(),
  outcome_code: z.string().nullable(),
  outcome_detail: z.string().nullable(),
  resolution: PublicationResolutionSchema.nullable(),
  retraction: PublicationRetractionSchema.nullable(),
  audit: PublicationAuditSchema.nullable(),
});
export type Publication = z.output<typeof PublicationSchema>;

/**
 * Модель рендера. Порядок полей — как в §9.1 и как отдаёт `read-model.ts`.
 *
 * Проверка `(canonical.media !== null) === (media_ref !== null)` не дублирует
 * инвариант `DraftContentSchema`: тот держит ДЕСКРИПТОР внутри
 * подписываемого контента, эта — АДРЕС байтов рядом с ним. Разъехаться они могут ровно
 * на одном баге — на выборке медиа мимо соединения, и тогда превью показало бы пост
 * без картинки как готовый к публикации.
 *
 * `topic` был `z.null()` всю фазу 1 — тем не существовало вовсе. В фазе 2 поле раскрыто
 * (`RenderTopicSchema`), и это не косметика: пока согласующий видел только текст, сценарий
 * «правдоподобная выдумка с открытой площадки» не ловился ничем — префильтр меряет
 * релевантность, а не истинность (06-security-ops §3). `nullable` остаётся законным и после
 * раскрытия: у фикстурного черновика темы нет и не будет, `draft.topic_id` в DDL nullable.
 */
export const RenderModelSchema = z
  .strictObject({
    draft_id: Uuid,
    /** Сквозной номер поста в бренде — см. `PostNo`. */
    post_no: PostNo,
    version_no: z.int().positive(),
    status: DraftState,
    brand: z.string().min(1),
    origin: DraftOrigin,
    fixture_id: z.string().nullable(),
    format: z.string().nullable(),
    format_rationale: z.string().nullable(),
    /** Стиль этой версии — см. `RenderStyleSchema`. */
    style: RenderStyleSchema.nullable(),
    /** Половина прогона сравнения стилей, если пост из него. */
    comparison: RenderComparisonSchema.nullable(),
    canonical: DraftContentSchema,
    media_ref: MediaRefSchema.nullable(),
    /** Документ версии; пара с `canonical.document` — проверка ниже. */
    document_ref: DocumentRefSchema.nullable().default(null),
    /**
     * ПОЛОСА ВИЗУАЛА (§6.2) — всё, что нужно, чтобы она сказала правду и словами.
     *
     * До этого поля интерфейс знал о картинке ровно одно: `media_ref` есть или нет. Этого
     * хватает, чтобы нарисовать картинку, и не хватает ни на что другое: «картинки нет» без
     * причины читается как сбой, и человек жмёт «сгенерировать» из подозрения, а не из
     * замысла. `reason` — та самая строка вердикта, `origin` отличает «сгенерирован
     * сервисом» от «загружено вами», а `proposal.state` объясняет, почему кнопка
     * утверждения недоступна, вместо того чтобы просто быть недоступной.
     *
     * `null` — законное состояние: у версий, рождённых до вердикта, его нет, и врать за них
     * нечем. Инвариант `media_ref_method_mismatch` ниже это поле НЕ трогает и трогать не
     * должно: предложение лежит СНАРУЖИ `canonical` намеренно, и втянуть его в проверку
     * пары «дескриптор ↔ адрес» значило бы гасить весь экран черновика (§9) из-за картинки,
     * которую ещё никто не принял.
     */
    visual: z
      .strictObject({
        verdict: z.enum(VISUAL_VERDICTS),
        reason: z.string(),
        origin: z.enum(MEDIA_ORIGINS).nullable(),
        proposal: z
          .strictObject({
            proposal_id: Uuid,
            media_id: Uuid,
            layout: VisualLayoutSchema,
            instead_of_text: z.string(),
            state: z.enum(VISUAL_PROPOSAL_STATES),
          })
          .nullable(),
        /**
         * Обложка ПРИКРЕПЛЁННОГО актива (docs/phase-2/10-posters.md) — спецификация из
         * `media_asset.spec_json`, когда картинка версии и есть обложка. По ней полоса
         * подписывает формат крючка, фон и прочтение поста, а диалог одобрения сверяет дату
         * с выбранным слотом до того, как это сделает бэкенд. `null` — картинки нет или это
         * не обложка; нет поля — ответ бэкенда старше обложки, и читается так же, как `null`.
         */
        poster: PosterSpecSchema.nullable().optional(),
      })
      .nullable(),
    /** Голый hex 64 символа, без префикса `sha256-`; UI кладёт его в expected_content_hash. */
    content_hash: ContentHash,
    canonicalization_diff: z.array(CanonicalizationDiffEntrySchema),
    raw_submitted: RawSubmittedSchema.nullable(),
    topic: RenderTopicSchema.nullable(),
    publication: PublicationSchema.nullable(),
    /**
     * Последние отправки ЭТОЙ версии в тестовый канал (§9.1), свежая первой, не больше трёх.
     * Статус версии они не меняют: превью не публикация.
     */
    test_sends: z.array(TestSendSchema).max(3).default([]),
    /**
     * След автоодобрения ЭТОЙ версии (docs/ai-lab.md): одобрило — экран говорит, что решения
     * человека не было; пропустило — почему. Ключа нет, если автоодобрение версии не касалось.
     */
    auto_approval: VersionAutoApprovalSchema.optional(),
    /**
     * Мнение автора канала у черновика (AGI Boardroom, `api/author-opinion.ts`) — данные черновика,
     * повторённые в модели каждой версии: что написано, во что очищено, кем и когда, и стоит ли
     * очищенный текст в ЭТОЙ версии дословно (`in_version`). `null` — мнения нет; нет ключа —
     * ответ бэкенда старше поля, читается так же. Необязательно намеренно: модель рендера
     * разбирается строго, и обязательное поле погасило бы экран черновика у всех брендов.
     */
    author_opinion: AuthorOpinionViewSchema.nullable().optional(),
  })
  .check((ctx) => {
    const v = ctx.value;
    // Условие переехало с метода на дескриптор, и инвариант от этого не изменился:
    // `sendRichMessage` покрывает и пост с картинкой, и длинный пост без неё, поэтому
    // метод больше не отвечает на вопрос «есть ли медиа». Отвечает `canonical.media`.
    if ((v.canonical.media !== null) !== (v.media_ref !== null)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['media_ref'],
        message: 'media_ref_method_mismatch',
      });
    }
    // Та же пара для документа: превью без файла при файле в подписываемом — ложь экрана.
    if ((v.canonical.document !== null) !== (v.document_ref !== null)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['document_ref'],
        message: 'document_ref_mismatch',
      });
    }
  });
export type RenderModel = z.output<typeof RenderModelSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/v1/schedule (09 §9.4)

/**
 * Строка материализованной сетки. Занятость определяется полем `state`, а НЕ наличием
 * `draft_id`: `skipped` и `passed` тоже не свободны, а `draft_id` у них пуст.
 */
export const ScheduleSlotSchema = z.strictObject({
  slot_id: Uuid,
  slot_at: IsoUtcMs,
  slot_local: z.string().regex(LOCAL_MINUTE_RE),
  state: z.enum(['free', 'taken', 'skipped', 'passed']),
  /** Локальная минута повторяется дважды при переводе часов назад — SlotPicker метит такие. */
  ambiguous: z.boolean(),
  draft_id: Uuid.nullable(),
  /** Номер поста в слоте (`PostNo`); пуст ровно тогда, когда пуст `draft_id`. */
  post_no: PostNo.nullable(),
  version_no: z.int().positive().nullable(),
  status: DraftState.nullable(),
  title: z.string().nullable(),
});
export type ScheduleSlot = z.output<typeof ScheduleSlotSchema>;

export const ScheduleResponseSchema = z.strictObject({
  /** Зона бренда: единственный законный путь зоны в UI (09 §9.4). */
  timezone: z.string().min(1),
  slots: z.array(ScheduleSlotSchema),
});
export type ScheduleResponse = z.output<typeof ScheduleResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Ответы решений человека
//
// Форма снята с кода домена (`domain/approve.ts`, `domain/decisions.ts`, `domain/retract.ts`),
// а не с таблицы §6.1: таблица перечисляет ответы сокращённо (у approve там два поля из
// пяти), и схема по ней отвергала бы настоящий ответ. `status` в каждом ответе — литерал:
// это состояние, в которое переход УЖЕ совершён, и любое другое значение означает, что
// сработал не тот переход.

export const ApproveResponseSchema = z.strictObject({
  status: z.literal('scheduled'),
  publication_id: Uuid,
  key_id: Uuid,
  /** Момент слота дословно, как его записал бэкенд, — UI не пересчитывает время. */
  slot_at: IsoUtcMs,
  /** Хэш, под которым выдан ключ; совпадает с показанным человеку. */
  content_hash: ContentHash,
});
export type ApproveResponse = z.output<typeof ApproveResponseSchema>;

export const RequestChangesResponseSchema = z.strictObject({
  status: z.literal('changes_requested'),
  rework_id: Uuid,
});
export type RequestChangesResponse = z.output<typeof RequestChangesResponseSchema>;

export const PostponeResponseSchema = z.strictObject({ status: z.literal('deferred') });
export type PostponeResponse = z.output<typeof PostponeResponseSchema>;

/**
 * «Удалить» — один маршрут на переходы 27–30 (09 §6.1; §13.2 закрывала его, пока состояния
 * `discarded` не существовало). Терминальное состояние, а не DELETE: строки версии, решений
 * и аудита остаются, из рабочего бэклога черновик уходит.
 */
export const DiscardResponseSchema = z.strictObject({ status: z.literal('discarded') });
export type DiscardResponse = z.output<typeof DiscardResponseSchema>;

/** Снятие со слота возвращает версию в `approved`, а не в `pending_review`: 09 §4.5. */
export const UnscheduleResponseSchema = z.strictObject({ status: z.literal('approved') });
export type UnscheduleResponse = z.output<typeof UnscheduleResponseSchema>;

/** Отзыв не меняет статус версии сразу: он открывает работу отзыва (09 §4.2). */
export const RetractResponseSchema = z.strictObject({
  retraction_id: Uuid,
  status: z.literal('open'),
});
export type RetractResponse = z.output<typeof RetractResponseSchema>;

export const ConfirmPublishedResponseSchema = z.strictObject({
  status: z.literal('published'),
  /** Номер сообщения, вынутый бэкендом из `post_url`; UI разбор ссылки не дублирует. */
  message_id: z.int().positive(),
});
export type ConfirmPublishedResponse = z.output<typeof ConfirmPublishedResponseSchema>;

export const ConfirmNotPublishedResponseSchema = z.strictObject({ status: z.literal('failed') });
export type ConfirmNotPublishedResponse = z.output<typeof ConfirmNotPublishedResponseSchema>;

/** Один маршрут на переходы 24, 23 и 10 — исход у всех трёх один (09 §13.3). */
export const ReopenResponseSchema = z.strictObject({ status: z.literal('pending_review') });
export type ReopenResponse = z.output<typeof ReopenResponseSchema>;
